// src/pages/profile-settings/patient/EditName.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import supabase from "@/services/supabaseClient";

import TopBar from "@/sot/primitives/TopBar.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import DoctorSalutationDropdown from "@/pages/shared/DoctorSalutationDropdown.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { PATIENT_SETTINGS_PROFILE_ROUTE } from "@/pages/profile-settings/settingsNavigation.js";
import { updateOwnAccountDisplayName } from "@/services/accountIdentityService.js";

import {
  loadPatientIntroPreview,
  savePatientIntroPreview,
} from "@/lib/patientIntroPreview";

const PATIENT_SALUTATION_OPTIONS = ["", "Mr.", "Mrs.", "Ms.", "Miss", "Mme.", "Prof."];

function isFrenchLanguageCode(languageCode) {
  return String(languageCode || "").toLowerCase().startsWith("fr");
}

// Simple helper to split a full name into first + last
function splitName(full = "") {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return {
    first: parts[0],
    last: parts.slice(1).join(" "),
  };
}

export default function EditName() {
  const nav = useNavigate();
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);

  const [first, setFirst] = React.useState("");
  const [last, setLast] = React.useState("");
  const [salutation, setSalutation] = React.useState("");
  const [userId, setUserId] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
  });

  const salutationOptions = React.useMemo(
    () =>
      PATIENT_SALUTATION_OPTIONS.map((option) => ({
        value: option,
        label: option
          ? t(`settings.edit.name.salutation.${option}`, option)
          : t("settings.edit.name.salutation.none", isFrench ? "Aucun titre" : "No title"),
      })),
    [isFrench, t],
  );

  React.useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      // 1) Seed from local preview if present
      const preview = loadPatientIntroPreview();
      if (PATIENT_SALUTATION_OPTIONS.includes(preview?.salutation) && !cancelled) {
        setSalutation(preview.salutation);
      }
      if (preview?.name && !cancelled) {
        const { first, last } = splitName(preview.name);
        setFirst(first);
        setLast(last);
      }

      // 2) Seed from Supabase full_name if still empty
      try {
        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError || !user || cancelled) return;
        setUserId(user.id);
        const scopedPreview = loadPatientIntroPreview(user.id) || {};
        if (PATIENT_SALUTATION_OPTIONS.includes(scopedPreview?.salutation) && !cancelled) {
          setSalutation(scopedPreview.salutation);
        }
        if (scopedPreview?.name && !cancelled) {
          const { first, last } = splitName(scopedPreview.name);
          setFirst(first);
          setLast(last);
        }

        {
          const { data: profile, error: profError } = await supabase
            .from("patient_profiles")
            .select("full_name,first_name,last_name,salutation")
            .eq("user_id", user.id)
            .maybeSingle();

          if (!profError && profile && !cancelled) {
            const { first, last } = profile.full_name
              ? splitName(profile.full_name)
              : { first: "", last: "" };
            setFirst((prev) => prev || first);
            setLast((prev) => prev || last);
            setFirst((prev) => prev || String(profile.first_name || "").trim());
            setLast((prev) => prev || String(profile.last_name || "").trim());
            if (PATIENT_SALUTATION_OPTIONS.includes(profile.salutation)) {
              setSalutation(profile.salutation || "");
            }
          }
        }
      } catch (e) {
        // dev mode / auth missing is fine for now
        console.warn("[Patient/EditName] bootstrap error:", e);
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const canSave =
    first.trim().length > 0 &&
    last.trim().length > 0 &&
    PATIENT_SALUTATION_OPTIONS.includes(salutation) &&
    !saving;

  async function handleSave() {
    if (!canSave) return;

    setSaving(true);
    const full_name = `${first.trim()} ${last.trim()}`.trim();

    try {
      // 1) Update local intro preview – keeps EditIntro in sync immediately
      const current = loadPatientIntroPreview(userId || "") || {};
      const nextPreview = {
        ...current,
        salutation,
        firstName: first.trim(),
        lastName: last.trim(),
        name: full_name,
        full_name,
      };
      savePatientIntroPreview(nextPreview, userId || "");

      // 2) Persist to Supabase when auth is available
      if (userId) {
        const { error } = await supabase
          .from("patient_profiles")
          .upsert({
            user_id: userId,
            first_name: first.trim(),
            last_name: last.trim(),
            full_name,
            salutation: salutation || null,
            updated_at: new Date().toISOString(),
          }, { onConflict: "user_id" });

        if (error) throw error;

        await updateOwnAccountDisplayName(full_name);

        // Best-effort metadata sync so app hydration and Fola copy do not fall back to stale names.
        try {
          await supabase.auth.updateUser({
            data: {
              full_name,
              first_name: first.trim(),
              last_name: last.trim(),
              salutation: salutation || null,
            },
          });
        } catch {
          // metadata sync is non-blocking
        }
      }

      setSnackbar({
        open: true,
        message: t("settings.edit.name.updated", isFrench ? "Nom mis à jour" : "Name updated"),
      });

      // Let the Snackbar show, then go back to EditIntro
      setTimeout(() => {
        setSnackbar((s) => ({ ...s, open: false }));
        nav(PATIENT_SETTINGS_PROFILE_ROUTE);
      }, 2200);
    } catch (e) {
      console.error("[Patient/EditName] save error:", e);
      setSnackbar({
        open: true,
        message: t(
          "settings.edit.common.saveFailed",
          isFrench ? "Impossible d’enregistrer les modifications. Veuillez réessayer." : "Could not save changes. Please try again."
        ),
      });

      setTimeout(() => {
        setSnackbar((s) => ({ ...s, open: false }));
      }, 2500);
    } finally {
      setSaving(false);
    }
  }

  const handleFirstChange = (v) =>
    setFirst(typeof v === "string" ? v : v?.target?.value ?? "");
  const handleLastChange = (v) =>
    setLast(typeof v === "string" ? v : v?.target?.value ?? "");

  return (
    <SettingsScreenShell>
      <TopBar
        title={t("settings.edit.name.title", isFrench ? "Modifier le nom" : "Edit Name")}
        subtitle={t(
          "settings.edit.name.subtitle",
          isFrench
            ? "Assurez-vous que ce nom correspond à celui figurant sur votre pièce d’identité officielle."
            : "Make sure this matches the name on your official government issued ID."
        )}
        onBack={() => nav(PATIENT_SETTINGS_PROFILE_ROUTE)}
        trailing={<SaveChip disabled={!canSave} onClick={handleSave} />}
      />

      {/* spacing under title */}
      <div className="mt-5 md:mt-6 space-y-6">
        <DoctorSalutationDropdown
          id="patient-settings-salutation"
          name="salutation"
          label={t("settings.edit.name.salutationLabel", isFrench ? "Titre" : "Title")}
          placeholder={t("settings.edit.name.patientSalutationPlaceholder", isFrench ? "Facultatif" : "Optional")}
          value={salutation}
          options={salutationOptions}
          required={false}
          tone="surface"
          onChange={(event) => {
            const next = event.target.value;
            setSalutation(PATIENT_SALUTATION_OPTIONS.includes(next) ? next : "");
          }}
        />

        {/* First name */}
        <div className="relative">
          <TextField56
            name="firstName"
            label={t("settings.edit.name.firstLabel", isFrench ? "Prénom sur la pièce d’identité" : "First name on ID")}
            value={first}
            onChange={handleFirstChange}
            placeholder={t(
              "settings.edit.name.firstPlaceholder",
              isFrench ? "Prénom(s) sur la pièce d’identité" : "First name/s on ID"
            )}
            autoComplete="given-name"
            inputMode="text"
            tone="surface"
            clearable={false}
          />
          {first.trim().length > 0 && (
            <button
              type="button"
              aria-label={t("settings.edit.name.clearFirst", isFrench ? "Effacer le prénom" : "Clear first name")}
              onClick={() => setFirst("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center"
              style={{
                width: 44,
                height: 44,
                borderRadius: 8,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <img
                src="/icons/doctor/cancel.svg"
                alt=""
                className="h-5 w-5"
              />
            </button>
          )}
        </div>

        {/* Last name */}
        <div className="relative">
          <TextField56
            name="lastName"
            label={t("settings.edit.name.lastLabel", isFrench ? "Nom de famille sur la pièce d’identité" : "Family name on ID")}
            value={last}
            onChange={handleLastChange}
            placeholder={t(
              "settings.edit.name.lastPlaceholder",
              isFrench ? "Nom(s) de famille sur la pièce d’identité" : "Family name/s on ID"
            )}
            autoComplete="family-name"
            inputMode="text"
            tone="surface"
            clearable={false}
          />
          {last.trim().length > 0 && (
            <button
              type="button"
              aria-label={t("settings.edit.name.clearLast", isFrench ? "Effacer le nom de famille" : "Clear family name")}
              onClick={() => setLast("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center"
              style={{
                width: 44,
                height: 44,
                borderRadius: 8,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <img
                src="/icons/doctor/cancel.svg"
                alt=""
                className="h-5 w-5"
              />
            </button>
          )}
        </div>
      </div>

      {/* Global snackbar for success + errors */}
      <Snackbar
        open={snackbar.open}
        onClose={() =>
          setSnackbar((s) => ({
            ...s,
            open: false,
          }))
        }
        duration={2200}
      >
        {snackbar.message}
      </Snackbar>
    </SettingsScreenShell>
  );
}
